import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import Modal from 'src/components/Modal';
import { MESSAGE_MODE } from 'src/constants/messageMode';
import { DEFAULT_MESSAGE_PRIORITY_VALUE } from 'src/constants/messagePriority';
import { MESSAGE_STATUS, SPECIAL_SENDER } from 'src/constants/messageStatus';
import useAuth from 'src/hooks/useAuth';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import useDeviceType from 'src/hooks/useDeviceType';
import usePortrait from 'src/hooks/usePortrait';
import useViewMessage from 'src/hooks/useViewMessage';
import useWindowSize from 'src/hooks/useWindowSize';
import useCreateMessageApi from 'src/services/messages_services/useCreateMessageApi';
import useDeleteMessagesApi from 'src/services/messages_services/useDeleteMessagesApi';
import { Message } from 'src/services/messages_services/useGetMessageApi';
import useMessageDetailsInboxApi, {
  MessageAttachment,
} from 'src/services/messages_services/useMessageDetailsInboxApi';
import useSetMessageToReadApi from 'src/services/messages_services/useSetMessageToReadApi';
import { toApiError } from 'src/utils/errorHandling';
import { sanitizePlainText } from 'src/utils/sanitization';
import BaseMessageDetailsModal from './BaseMessageDetailsModal';
import MessagePopUpContentWrapper from './ComposeMessagePopUpContentWrapper';
import { useInboxMessageDetails } from './hooks/useInboxMessageDetails';

interface MessageDetailsInboxModalProps {
  visible: boolean;
  closeModal: () => void;
  message: Message | null;
}

const MessageDetailsInboxModal: FC<MessageDetailsInboxModalProps> = ({
  visible,
  closeModal,
  message,
}) => {
  const { t } = useTranslation();
  const { company } = useCompany();
  const { clientId: authClientId } = useAuth();
  const { confirm } = useConfirm();
  const { refreshTable } = useViewMessage();
  const { width } = useWindowSize();
  const deviceType = useDeviceType();
  const isPortrait = usePortrait();

  const [currentMessageStatus, setCurrentMessageStatus] = useState<string | undefined>(
    message?.decodedStatus
  );
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [replySubject, setReplySubject] = useState('');

  // Responsive modal width for Reply modal
  const replyModalWidth = useMemo(
    () =>
      deviceType !== 'desktop'
        ? isPortrait
          ? `${width}px`
          : `${Math.min(width, 700)}px`
        : '700px',
    [deviceType, isPortrait, width]
  );

  // Initialize API hooks
  const { createMessage } = useCreateMessageApi();
  const clientIdForDelete = authClientId || company?.companyId || '';
  const messageIdsForDelete = message?.messageId ? [message.messageId] : [];
  const { deleteMessage } = useDeleteMessagesApi(
    clientIdForDelete,
    messageIdsForDelete,
    true,
    MESSAGE_MODE.INBOX,
    message ? [message] : []
  );

  // Update local status when message prop changes
  useEffect(() => {
    setCurrentMessageStatus(message?.decodedStatus);
  }, [message?.decodedStatus]);

  // Reply modal handlers
  const openReplyModal = () => {
    // Auto-generate reply subject with 'Re: ' prefix, trimmed to 255 characters
    if (message?.subject) {
      const prefix = 'Re: ';
      const maxLength = 255;
      const fullSubject = `${prefix}${message.subject}`;

      if (fullSubject.length <= maxLength) {
        setReplySubject(fullSubject);
      } else {
        // Trim the original subject to fit within limit
        const availableLength = maxLength - prefix.length;
        setReplySubject(`${prefix}${message.subject.substring(0, availableLength)}`);
      }
    } else {
      setReplySubject('');
    }

    setIsReplyModalOpen(true);
    setReplyMessage('');
  };

  const closeReplyModal = () => {
    setIsReplyModalOpen(false);
    setReplyMessage('');
    setReplySubject('');
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !replySubject.trim() || !message) {
      toast.error(t('Please enter a reply subject and message'));
      return;
    }

    // Validate subject length (max 255 characters)
    if (replySubject.trim().length > 255) {
      toast.error(t('Subject cannot exceed 255 characters'));
      return;
    }

    // Validate message body length (max 4000 characters)
    if (replyMessage.trim().length > 4000) {
      toast.error(t('Message body cannot exceed 4,000 characters'));
      return;
    }

    try {
      const clientId = authClientId
        ? parseInt(authClientId, 10)
        : parseInt(company?.companyId || '0', 10);

      if (!clientId || isNaN(clientId)) {
        console.error('No valid clientId available for sending reply');
        toast.error(t('Unable to send reply: No valid client selected'));
        return;
      }

      // For inbox, reply should go ONLY to sender's clientId (messageDetails.clientId or message.createdBy)
      const senderClientId =
        messageDetails?.clientId &&
        messageDetails.clientId !== 'null' &&
        messageDetails.clientId !== 'undefined'
          ? messageDetails.clientId
          : message?.createdBy && message.createdBy !== 'null' && message.createdBy !== 'undefined'
          ? message.createdBy
          : null;

      console.log('Reply will be sent to clientId:', senderClientId);

      if (!senderClientId) {
        toast.error('Cannot determine sender clientId for reply.');
        return;
      }

      const requestBody = {
        Subject: sanitizePlainText(replySubject),
        Body: sanitizePlainText(replyMessage.trim()),
        ClientId: clientId,
        SenderClientId: clientId,
        ProxyClientId: clientId,
        Priority: DEFAULT_MESSAGE_PRIORITY_VALUE,
        IsReply: true,
        RecipientClientIds: [Number(senderClientId)],
      };

      await createMessage(requestBody);
      setReplyMessage('');
      setIsReplyModalOpen(false);
      closeModal();

      toast.success(t('Reply sent successfully'));
      setCurrentMessageStatus(MESSAGE_STATUS.REPLIED);
      refreshTable();
    } catch (error: unknown) {
      const apiError = toApiError(error);
      console.error('Failed to send reply:', apiError);
      const errorMessage =
        apiError.response?.data?.message ||
        apiError.response?.data?.errors?.Body?.[0] ||
        apiError.message ||
        t('Failed to send reply');
      toast.error(errorMessage);
    }
  };

  const handleDeleteMessage = useCallback(async () => {
    if (!message) return;

    const confirmed = await confirm({
      content: t('Are you sure you want to delete this message?'),
      title: t('Confirm Delete'),
    });

    if (!confirmed) return;

    try {
      await deleteMessage();
      toast.success(t('Message deleted successfully'));
      setCurrentMessageStatus(MESSAGE_STATUS.DELETED);
      closeModal();
      refreshTable();
    } catch (error: unknown) {
      const apiError = toApiError(error);
      console.error('Delete message failed:', apiError);
      const errorMessage =
        apiError.response?.data?.message || apiError.message || t('Failed to delete message');
      toast.error(errorMessage);
    }
  }, [message, confirm, t, deleteMessage, closeModal, refreshTable]);

  // Fetch message details using custom hook
  const messageIdString = message?.messageId?.toString();
  const shouldUseApi = Boolean(visible && messageIdString);
  const { getMessage: getMessageDetails, isMessageLoading } = useMessageDetailsInboxApi(
    shouldUseApi ? messageIdString! : 'NO_MESSAGE'
  );
  const { setMessageToRead } = useSetMessageToReadApi();

  // Use custom hook to manage message details and mark as read
  const { messageDetails, detailsError } = useInboxMessageDetails({
    visible,
    message,
    messageIdString: messageIdString || '',
    shouldUseApi,
    authClientId: authClientId || '',
    getMessageDetails,
    setMessageToRead,
    setCurrentMessageStatus,
    refreshTable,
    isMessageLoading,
  });

  // Consolidated status checks to avoid repetition (must be before early return)
  const lowercaseStatus = message?.decodedStatus?.toLowerCase() || '';
  const lowercaseCreatedBy = message?.createdByUserName?.toLowerCase() || '';

  const currentStatus = useMemo(
    () => currentMessageStatus?.toLowerCase() || lowercaseStatus,
    [currentMessageStatus, lowercaseStatus]
  );
  const isDeleted = useMemo(() => currentStatus === MESSAGE_STATUS.DELETED, [currentStatus]);
  const isDeletedByRecipient = useMemo(
    () => currentStatus === MESSAGE_STATUS.DELETED_BY_RECIPIENT,
    [currentStatus]
  );
  const isDeletedBySender = useMemo(
    () => currentStatus === MESSAGE_STATUS.DELETED_BY_SENDER,
    [currentStatus]
  );
  const isSystemMessage = useMemo(
    () => lowercaseCreatedBy === SPECIAL_SENDER.SYSTEM_PROCESS,
    [lowercaseCreatedBy]
  );

  if (!message) return null;

  // Render action buttons for inbox (reply, delete)
  const renderActions = () => {
    if (isDeleted || isDeletedByRecipient) {
      return null;
    }

    return (
      <>
        <div
          className="relative"
          title={
            isSystemMessage
              ? t('Reply to this sender not permitted')
              : isDeletedBySender
              ? t('Reply to sender not permitted when sender deleted the message')
              : isDeletedByRecipient
              ? t('Reply to sender not permitted when recipient deleted the message')
              : undefined
          }
        >
          <Button
            color="primary"
            size="md"
            onClick={
              !isSystemMessage && !isDeletedBySender && !isDeletedByRecipient
                ? openReplyModal
                : undefined
            }
            disabled={isSystemMessage || isDeletedBySender || isDeletedByRecipient}
          >
            {t('Reply')}
          </Button>
        </div>
        <div
          className="relative"
          title={
            isDeletedByRecipient
              ? t('Delete not permitted when recipient already deleted the message')
              : undefined
          }
        >
          <Button
            color="error"
            size="md"
            onClick={!isDeletedByRecipient ? handleDeleteMessage : undefined}
            disabled={isDeletedByRecipient}
          >
            {t('Delete')}
          </Button>
        </div>
      </>
    );
  };

  // Render contact info (sender details) for inbox
  const renderContactInfo = () => (
    <>
      {/* Sender Name */}
      <div>
        <label className="block text-sm font-medium text-blue-800 mb-1">{t('Sender')}</label>
        <div className="text-gray-900 text-sm">
          {sanitizePlainText(
            message?.createdByUserName
              ? message.createdByUserName.toLowerCase() === SPECIAL_SENDER.SYSTEM_PROCESS
                ? message.createdByUserName
                : message.createdByUserName
              : t('N/A')
          )}
        </div>
      </div>

      {/* Sender Info */}
      <div>
        <span className="text-sm font-medium text-blue-800">{t('Sender Information')}</span>
      </div>

      {/* Contact Details */}
      <div
        className={`${
          lowercaseCreatedBy === SPECIAL_SENDER.SYSTEM_PROCESS ? 'bg-transparent' : 'bg-blue-50'
        } p-4 rounded-md border border-blue-200`}
        style={{ maxWidth: '550px' }}
      >
        {isMessageLoading && !messageDetails ? (
          <div className="text-gray-500 text-sm">{t('Loading sender details...')}</div>
        ) : detailsError ? (
          <div className="text-gray-500 text-sm italic">{detailsError}</div>
        ) : (
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-xs font-medium text-blue-800 mb-1">
                {t('Sender Company')}
              </label>
              <div className="text-gray-900 text-sm">
                {message?.recipientCompanyName || t('N/A')}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-800 mb-1">
                {t('Sender Email')}
              </label>
              <div className="text-gray-900 text-sm">{t('N/A')}</div>
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-800 mb-1">
                {t('Sender Phone Number')}
              </label>
              <div className="text-gray-900 text-sm">
                {messageDetails?.telecomNumber1 || t('N/A')}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );

  // Render additional content (attachments) for inbox
  const renderAdditionalContent = () => (
    <div className="hidden">
      <label className="block text-sm font-medium text-blue-800 mb-1">{t('Attachments')}</label>
      <div className="bg-gray-50 p-4 rounded-md border">
        {message.attachmentsCount > 0 ? (
          <div className="space-y-1">
            {isMessageLoading ? (
              <div className="text-gray-500 text-sm">{t('Loading attachments...')}</div>
            ) : messageDetails?.attachments && messageDetails.attachments.length > 0 ? (
              messageDetails.attachments
                .filter((attachment) => attachment)
                .map((attachment: MessageAttachment, index: number) => {
                  const actualFileName =
                    attachment?.fileName ||
                    attachment?.name ||
                    attachment?.originalName ||
                    attachment?.originalFile ||
                    attachment?.attachmentName ||
                    attachment?.filename;

                  const displayName =
                    actualFileName ||
                    `Message_${message.messageId}_Attachment_${attachment?.attachId || index}`;

                  const hasExtension = displayName?.includes?.('.');
                  const fileExtension = hasExtension
                    ? displayName.substring(displayName.lastIndexOf('.'))
                    : index % 2 === 0
                    ? '.pdf'
                    : '.jpg';
                  const fullDisplayName = hasExtension
                    ? displayName
                    : `${displayName}${fileExtension}`;

                  const iconColor = fileExtension.toLowerCase().includes('.pdf')
                    ? 'text-red-500'
                    : 'text-blue-500';
                  const icon = fileExtension.toLowerCase().includes('.pdf') ? '📄' : '🖼️';

                  return (
                    <div
                      key={attachment?.attachId || index}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <span className={`${iconColor} mr-2`}>{icon}</span>
                      <span
                        className="text-sm underline cursor-default"
                        title={`${t('Attachment ID')}: ${attachment?.attachId || 'N/A'}, ${t(
                          'Created'
                        )}: ${attachment?.creationDate || 'N/A'}`}
                      >
                        {fullDisplayName}
                      </span>
                    </div>
                  );
                })
            ) : (
              Array.from({ length: message.attachmentsCount }, (_, index) => (
                <div key={index} className="flex items-center text-blue-600 hover:text-blue-800">
                  <span className="text-gray-500 mr-2">📎</span>
                  <span className="text-sm text-gray-600">Attachment_{index + 1}</span>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="text-gray-500 text-sm">{t('No attachments')}</div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <BaseMessageDetailsModal
        visible={visible}
        closeModal={closeModal}
        message={message}
        mode={MESSAGE_MODE.INBOX}
        title={
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faEnvelope} className="text-white" />
            {t('Inbox Message Details')}
          </div>
        }
        currentStatus={currentMessageStatus}
        isContactInfoExpanded={true}
        renderActions={renderActions}
        renderContactInfo={renderContactInfo}
        renderAdditionalContent={renderAdditionalContent}
      />

      {/* Reply Modal */}
      {isReplyModalOpen && (
        <Modal
          visible={isReplyModalOpen}
          closeModal={closeReplyModal}
          title={
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faEnvelope} className="text-white" />
              {t('Reply to Message')}
            </div>
          }
          width={replyModalWidth}
        >
          <div className="px-6 pt-3 pb-6 space-y-4">
            <div className="message-layout flex-center">
              <div className="relative flex flex-col items-center">
                <PopUp
                  show={
                    !replyMessage.trim() ||
                    !replySubject.trim() ||
                    replyMessage.length > 4000 ||
                    replySubject.length > 255
                  }
                  className="compose-error-popup"
                  style={{ border: 'none', boxShadow: 'none', backgroundColor: 'transparent' }}
                  content={
                    <div
                      className="mt-2"
                      style={{ border: 'none', boxShadow: 'none', backgroundColor: 'transparent' }}
                    >
                      <MessagePopUpContentWrapper
                        type="Error"
                        contents={[
                          ...(!replyMessage.trim() || !replySubject.trim()
                            ? [t('Please provide values in all required fields')]
                            : []),
                          ...(replySubject.length > 255
                            ? [t('Subject exceeds 255 character limit')]
                            : []),
                          ...(replyMessage.length > 4000
                            ? [t('Message exceeds 4,000 character limit')]
                            : []),
                        ]}
                        prefix=""
                      />
                    </div>
                  }
                >
                  <Button
                    color="primary"
                    onClick={handleSendReply}
                    disabled={
                      !replyMessage.trim() ||
                      !replySubject.trim() ||
                      replyMessage.length > 4000 ||
                      replySubject.length > 255
                    }
                  >
                    {t('Send Reply')}
                  </Button>
                </PopUp>
              </div>
              <Button color="secondary" onClick={closeReplyModal}>
                {t('Cancel')}
              </Button>
            </div>

            <div>
              <label className="message-label">{t('Reply To')}</label>
              <div className="text-lg font-semibold text-gray-900">
                {sanitizePlainText(message?.createdByUserName || '')}
              </div>
            </div>

            <div>
              <label className="message-label">
                <span className="text-xs text-gray-400">
                  {t('Subject')} <span className="text-red-500">*</span>
                </span>
                <span className="ml-2 italic text-gray-400 text-xs align-middle">
                  {t('(limit 255 characters)')}
                </span>
                {replySubject.length >= 255 && (
                  <span className="ml-2 italic text-red-500 text-xs align-middle font-semibold">
                    {t('Exceeded limit')}
                  </span>
                )}
              </label>
              <textarea
                value={replySubject}
                onChange={(e) => setReplySubject(e.target.value)}
                placeholder={t('Enter subject...')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                style={{ maxWidth: '250px', height: '50px' }}
                maxLength={255}
              />
            </div>

            <div>
              <label className="message-label">
                <span className="text-xs text-gray-400">
                  {t('Message')} <span className="text-red-500">*</span>
                </span>
                <span className="ml-2 italic text-gray-400 text-xs align-middle">
                  {t('(limit 4,000 characters)')}
                </span>
                {replyMessage.length >= 4000 && (
                  <span className="ml-2 italic text-red-500 text-xs align-middle font-semibold">
                    {t('Exceeded limit')}
                  </span>
                )}
              </label>
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder={t('Type your reply here...')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                style={{ maxWidth: '250px' }}
                rows={8}
                maxLength={4000}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default MessageDetailsInboxModal;
